/**
 * PageOutline — "On this page" TOC.
 *
 * Renders a list of anchor links for the H2 sections on the current page.
 * Each item targets a `keyword` so the visible label is keyword-targeted
 * for SEO; the corresponding section heading should reuse the same wording.
 *
 * Usage:
 *   <PageOutline
 *     items={[
 *       { id: "custom-awnings-seguin", label: "Custom Awnings in Seguin, TX" },
 *       ...
 *     ]}
 *   />
 *
 * Then on the matching section:
 *   <section id="custom-awnings-seguin">
 *     <h2>Custom Awnings in Seguin, TX</h2>
 *   </section>
 */

export interface PageOutlineItem {
  id: string;
  label: string;
}

export interface PageOutlineProps {
  items: PageOutlineItem[];
  heading?: string;
  tone?: "cream" | "white" | "navy";
  className?: string;
  /** Optional image rendered beside the outline list (e.g. a service photo). */
  imageSrc?: string;
  imageAlt?: string;
}

export function PageOutline({
  items,
  heading = "On this page",
  tone = "cream",
  className = "",
  imageSrc,
  imageAlt = "",
}: PageOutlineProps) {
  if (!items || items.length === 0) return null;

  const toneClasses =
    tone === "navy"
      ? "bg-navy-deep text-white border-white/15"
      : tone === "white"
        ? "bg-white text-navy-deep border-navy-deep/15"
        : "bg-cream text-navy-deep border-navy-deep/15";

  const isDark = tone === "navy";

  return (
    <nav
      aria-label={heading}
      className={`border-b-8 border-navy-deep py-10 ${toneClasses} ${className}`}
    >
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <div
              className={`font-display text-xs uppercase tracking-[0.3em] ${
                isDark ? "text-red" : "text-red"
              }`}
            >
              ★ {heading}
            </div>
            <ol className="mt-4 grid list-none grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((it, i) => (
                <li key={it.id} className="flex items-baseline gap-3">
                  <span
                    className={`font-display text-xs tabular-nums ${
                      isDark ? "text-white/75" : "text-navy-deep/70"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <a
                    href={`#${it.id}`}
                    className={`font-display text-sm font-semibold leading-snug underline-offset-4 transition hover:text-red hover:underline ${
                      isDark ? "text-white" : "text-navy-deep"
                    }`}
                  >
                    {it.label}
                  </a>
                </li>
              ))}
            </ol>
          </div>
          {imageSrc ? (
            <figure className="hidden lg:block lg:w-72 xl:w-80">
              <img
                src={imageSrc}
                alt={imageAlt}
                loading="lazy"
                decoding="async"
                width={320}
                height={240}
                className="aspect-[4/3] w-full border-4 border-navy-deep object-cover shadow-lg"
              />
            </figure>
          ) : null}
        </div>
      </div>
    </nav>
  );
}
